import { StrictMode } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { Layout } from './components/layout/Layout';
import { checkVersion } from './utils/versionCheck';
import { Dashboard } from './pages/Dashboard';
import { BulkAccommodationStatusPage } from './pages/BulkAccommodationStatusPage';
import { TeamPage } from './pages/TeamPage';
import { RolesPage } from './pages/RolesPage';
import { OrganizersPage } from './pages/OrganizersPage';
import { OrganizersOverviewPage } from './pages/OrganizersOverviewPage';
import { OperatorOverviewPage } from './pages/OperatorOverviewPage';
import { GuidesPage } from './pages/GuidesPage';
import { PilgrimsPage } from './pages/PilgrimsPage';
import { PackagesPage } from './pages/PackagesPage';
import { FlightsPage } from './pages/FlightsPage';
import { VerificationPage } from './pages/VerificationPage';
import { VerificationWizardPage } from './pages/VerificationWizardPage';
import { VerificationDetailsPage } from './pages/VerificationDetailsPage';
import { VerificationApprovalPage } from './pages/VerificationApprovalPage';
import { PilgrimGuidePage } from './pages/PilgrimGuidePage';
import { BulkLinkStatusPage } from './pages/BulkLinkStatusPage';
import { BulkAnswerStatusPage } from './pages/BulkAnswerStatusPage';
import BulkOrganizerAnswerStatusPage from './pages/BulkOrganizerAnswerStatusPage';
import { LoginScreen } from './components/auth/LoginScreen';
import { LandingPage } from './pages/LandingPage';
import { LanguageProvider } from './contexts/LanguageContext';
import { Toaster } from 'react-hot-toast';
import { LinkingStatusPage } from './pages/LinkingStatusPage';
import { useInitialData } from './hooks/useInitialData';
import { LoadingSpinner } from './components/common/LoadingSpinner';
import { DisputesPage } from './pages/DisputesPage';
import { AccommodationPage } from './pages/AccommodationPage';
import { RoomViewPage } from './pages/RoomViewPage';
import { HotelVisualizationPage } from './pages/HotelVisualizationPage';
import { AccommodationTasksPage } from './pages/AccommodationTasksPage';
import { MovementSchedulePage } from './pages/MovementSchedulePage';
import { CampAccommodationPage } from './pages/CampAccommodationPage';
import { CampDetailsPage } from './pages/CampDetailsPage';
import { SettingsPage } from './pages/SettingsPage';
import { InstallPrompt } from './components/common/InstallPrompt';
import { AccommodationLogsPage } from './pages/AccommodationLogsPage';
import { BansManagementPage } from './pages/BansManagementPage';
import { OrganizerFinancePage } from './pages/OrganizerFinancePage';
import { TimelinePage } from './pages/TimelinePage';
import { RoomUpgradePage } from './pages/RoomUpgradePage';
import { BulkUpgradeStatusPage } from './pages/BulkUpgradeStatusPage';
import { MyOperationListPage } from './pages/MyOperationListPage';
import { OperationTakerPage } from './pages/OperationTakerPage';
import { PassportVisaPage } from './pages/PassportVisaPage';
import { BulkEasyAccommodationStatusPage } from './pages/BulkEasyAccommodationStatusPage';
import { BulkOperatorTransferStatusPage } from './pages/BulkOperatorTransferStatusPage';
import { OperatorHelperPage } from './pages/OperatorHelperPage';
import PilgrimsInformationsPage from './pages/PilgrimsInformationsPage';
import RoomTypeSwitchPage from './pages/RoomTypeSwitchPage';

export function App() {
  const [showLanding, setShowLanding] = useState(() => {
    return !localStorage.getItem('hasSeenLanding');
  });

  const { isLoading } = useInitialData();

  useEffect(() => {
    checkVersion();
  }, []);

  if (isLoading) return <LoadingSpinner />;

  if (showLanding) {
    return (
      <LanguageProvider>
        <LandingPage
          onStart={() => {
            setShowLanding(false);
            localStorage.setItem('hasSeenLanding', 'true');
          }}
        />
        <InstallPrompt />
        <Toaster 
          position="top-center"
          containerStyle={{
            zIndex: 99999
          }}
        />
      </LanguageProvider>
    );
  }

  return (
    <LanguageProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<LoginScreen onLogin={() => {}} />} />
          <Route
            path="*"
            element={
              <Layout>
                <Routes>
                  <Route path="/" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
                  <Route path="/timeline" element={<ProtectedRoute><TimelinePage /></ProtectedRoute>} />
                  <Route path="/pilgrims" element={<ProtectedRoute><PilgrimsPage /></ProtectedRoute>} />
                  <Route path="/pilgrims-informations" element={<ProtectedRoute><PilgrimsInformationsPage /></ProtectedRoute>} />
                  <Route path="/packages" element={<ProtectedRoute><PackagesPage /></ProtectedRoute>} />
                  <Route path="/flights" element={<ProtectedRoute><FlightsPage /></ProtectedRoute>} />
                  <Route path="/verification" element={<ProtectedRoute><VerificationPage /></ProtectedRoute>} />
                  <Route path="/verification-wizard" element={<ProtectedRoute><VerificationWizardPage /></ProtectedRoute>} />
                  <Route path="/verification-details/:id" element={<ProtectedRoute><VerificationDetailsPage /></ProtectedRoute>} />
                  <Route path="/verification-approval" element={<ProtectedRoute><VerificationApprovalPage /></ProtectedRoute>} />
                  <Route path="/pilgrim-guide" element={<ProtectedRoute><PilgrimGuidePage /></ProtectedRoute>} />
                  <Route path="/my-operation-list" element={<ProtectedRoute><MyOperationListPage /></ProtectedRoute>} />
                  <Route path="/operator-helper" element={<ProtectedRoute><OperatorHelperPage /></ProtectedRoute>} />
                  <Route path="/operation-taker" element={<ProtectedRoute><OperationTakerPage /></ProtectedRoute>} />
                  <Route path="/linking-status" element={<ProtectedRoute><LinkingStatusPage /></ProtectedRoute>} />
                  <Route path="/bulk-link-status" element={<ProtectedRoute><BulkLinkStatusPage /></ProtectedRoute>} />
                  <Route path="/bulk-accommodation-status" element={<ProtectedRoute><BulkAccommodationStatusPage /></ProtectedRoute>} />
                  <Route path="/bulk-easy-accommodation-status" element={<ProtectedRoute><BulkEasyAccommodationStatusPage /></ProtectedRoute>} />
                  <Route path="/bulk-operator-transfer-status" element={<ProtectedRoute><BulkOperatorTransferStatusPage /></ProtectedRoute>} />
                  <Route path="/bulk-upgrade-status" element={<ProtectedRoute><BulkUpgradeStatusPage /></ProtectedRoute>} />
                  <Route path="/bulk-answer-status" element={<ProtectedRoute><BulkAnswerStatusPage /></ProtectedRoute>} />
                  <Route path="/bulk-organizer-answer-status" element={<ProtectedRoute><BulkOrganizerAnswerStatusPage /></ProtectedRoute>} />
                  <Route path="/disputes" element={<ProtectedRoute><DisputesPage /></ProtectedRoute>} />
                  <Route path="/accommodation" element={<ProtectedRoute><AccommodationPage /></ProtectedRoute>} />
                  <Route path="/accommodation/:tableId" element={<ProtectedRoute><RoomViewPage /></ProtectedRoute>} />
                  <Route path="/hotel-visualization" element={<ProtectedRoute><HotelVisualizationPage /></ProtectedRoute>} />
                  <Route path="/accommodationTasks" element={<ProtectedRoute><AccommodationTasksPage /></ProtectedRoute>} />
                  <Route path="/movements" element={<ProtectedRoute><MovementSchedulePage /></ProtectedRoute>} />
                  <Route path="/camp-accommodation" element={<ProtectedRoute><CampAccommodationPage /></ProtectedRoute>} />
                  <Route path="/camp-accommodation/:tableId" element={<ProtectedRoute><CampDetailsPage /></ProtectedRoute>} />
                  <Route path="/team" element={<ProtectedRoute><TeamPage /></ProtectedRoute>} />
                  <Route path="/roles" element={<ProtectedRoute><RolesPage /></ProtectedRoute>} />
                  <Route path="/bans" element={<ProtectedRoute><BansManagementPage /></ProtectedRoute>} />
                  <Route path="/organizers" element={<ProtectedRoute><OrganizersPage /></ProtectedRoute>} />
                  <Route path="/guides" element={<ProtectedRoute><GuidesPage /></ProtectedRoute>} />
                  <Route path="/organizers-overview" element={<ProtectedRoute><OrganizersOverviewPage /></ProtectedRoute>} />
                  <Route path="/operator-overview" element={<ProtectedRoute><OperatorOverviewPage /></ProtectedRoute>} />
                  <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
                  <Route path="/accommodation-logs" element={<ProtectedRoute><AccommodationLogsPage /></ProtectedRoute>} />
                  <Route path="/organizer-finance" element={<ProtectedRoute><OrganizerFinancePage /></ProtectedRoute>} />
                  <Route path="/room-upgrade" element={<ProtectedRoute><RoomUpgradePage /></ProtectedRoute>} />
                  <Route path="/passport-visa" element={<ProtectedRoute><PassportVisaPage /></ProtectedRoute>} />
                  <Route path="/room-type-switch" element={<ProtectedRoute><RoomTypeSwitchPage /></ProtectedRoute>} />
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </Layout>
            }
          />
        </Routes>
        <InstallPrompt />
        <Toaster 
          position="top-center"
          containerStyle={{
            zIndex: 99999
          }}
        />
      </BrowserRouter>
    </LanguageProvider>
  );
}

// Protected Route Component
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const location = useLocation();
  const userId = localStorage.getItem('userId');
  const userRole = localStorage.getItem('userRole');
  const roleId = localStorage.getItem('roleId');

  // Check if user is logged in
  if (!userId || !userRole) {
    return <Navigate to="/login" replace />;
  }

  // Allow access to verification wizard, login and root pages
  if (
    location.pathname === '/verification-wizard' ||
    location.pathname === '/login' ||
    location.pathname === '/' ||
    location.pathname === '/guides' ||
    location.pathname === '/settings' ||
    location.pathname.startsWith('/verification-details/')
  ) {
    return <>{children}</>;
  }

  return <>{children}</>;
};